import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useGetProductsByIdsQuery } from "../../api/productApi";
import ProductCard from "./ProductCard/ProductCard";
import ProductCardSkeleton from "./ProductCard/ProductCardSkeleton";

type SwiperBreakpoints = {
  [width: number]: {
    slidesPerView: number;
    spaceBetween: number;
  };
};

interface RelatedProductsProps {
  productIds: number[];
  title: string;
  onQuickViewClick: (productId: number) => void;
  breakpoints?: SwiperBreakpoints;
  showNavigation?: boolean;
  showPagination?: boolean;
}

const RelatedProductsSkeleton: React.FC = () => (
  <div className="related-products__grid">
    {Array.from({ length: 4 }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

const defaultBreakpoints: SwiperBreakpoints = {
  320: { slidesPerView: 1, spaceBetween: 20 },
  540: { slidesPerView: 2, spaceBetween: 20 },
  768: { slidesPerView: 3, spaceBetween: 30 },
  1024: { slidesPerView: 4, spaceBetween: 30 },
};

const RelatedProducts: React.FC<RelatedProductsProps> = ({
  productIds,
  title,
  onQuickViewClick,
  breakpoints,
  showNavigation = true,
  showPagination = true,
}) => {
  const { ready: areTranslationsReady } = useTranslation();

  const {
    data: products,
    isLoading,
    isError,
  } = useGetProductsByIdsQuery(productIds, {
    skip: productIds.length === 0,
  });

  const isComponentLoading = isLoading || !areTranslationsReady;

  if (
    productIds.length === 0 ||
    isError ||
    (!isComponentLoading && (!products || products.length === 0))
  ) {
    return null;
  }

  const activeBreakpoints = breakpoints || defaultBreakpoints;

  const modules = [
    showNavigation ? Navigation : null,
    showPagination ? Pagination : null,
  ].filter((m) => m !== null) as any[];

  return (
    <section className="related-products">
      <h2 className="related-products__title">
        {isComponentLoading ? <Skeleton width={250} /> : title}
      </h2>
      {isComponentLoading ? (
        <RelatedProductsSkeleton />
      ) : (
        <Swiper
          modules={modules}
          spaceBetween={30}
          slidesPerView={4}
          navigation={showNavigation}
          pagination={showPagination ? { clickable: true } : false}
          className="related-products__slider"
          breakpoints={activeBreakpoints}
        >
          {products?.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCard
                product={product}
                view="grid"
                onQuickViewClick={onQuickViewClick}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </section>
  );
};

export default RelatedProducts;
